import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Monitor, Play, ChevronLeft, ChevronRight, Tv, Building2, Clock, Wifi, Upload, Settings, Eye, Volume2, VolumeX, Pause } from 'lucide-react';

const DEMO_IMAGES = [
  {
    url: '/frostbyte_signage_ad.png',
    caption: 'Brand Advertisement'
  },
  {
    url: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop&q=60',
    caption: 'Business Promotions'
  },
  {
    url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&auto=format&fit=crop&q=60',
    caption: 'Corporate Announcements'
  }
];

const WORKFLOW_STEPS = [
  {
    step: 1,
    title: 'Login to Admin',
    description: 'Secure password-protected access to your dashboard',
    icon: Settings
  },
  {
    step: 2,
    title: 'Upload Content',
    description: 'Drag & drop images or videos up to 1GB',
    icon: Upload
  },
  {
    step: 3,
    title: 'Watch it Display',
    description: 'Content appears on your screens instantly',
    icon: Eye
  }
];

const DigitalSignage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showVideoDemo, setShowVideoDemo] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPaused, setVideoPaused] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isPlaying) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % DEMO_IMAGES.length);
    }, 4000);
    
    return () => clearInterval(timer);
  }, [isPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % DEMO_IMAGES.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + DEMO_IMAGES.length) % DEMO_IMAGES.length);
  };

  const playVideoDemo = () => {
    setShowVideoDemo(true);
    setVideoPaused(false);
    // Start video and audio together
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      if (audioRef.current && !videoMuted) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    }, 100);
  };

  const toggleMute = () => {
    setVideoMuted(!videoMuted);
    if (audioRef.current) {
      if (videoMuted) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  };

  const togglePause = () => {
    setVideoPaused(!videoPaused);
    if (videoRef.current) {
      if (videoPaused) {
        videoRef.current.play();
        if (audioRef.current && !videoMuted) audioRef.current.play();
      } else {
        videoRef.current.pause();
        if (audioRef.current) audioRef.current.pause();
      }
    }
  };

  const closeVideoDemo = () => {
    setShowVideoDemo(false);
    if (videoRef.current) videoRef.current.pause();
    if (audioRef.current) audioRef.current.pause();
  };

  return (
    <section id="digital-signage" className="py-20 bg-gradient-to-b from-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-4">
            <Tv className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">NEW SERVICE</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Digital <span className="text-cyan-400">Signage</span> Solutions
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Transform your business with professional digital displays. Perfect for lobbies, waiting rooms, restaurants, and retail spaces.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Demo Display */}
          <div className="relative">
            {/* TV Frame */}
            <div className="relative bg-slate-800 rounded-2xl p-3 shadow-2xl">
              {/* Screen bezel */}
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                {/* Content */}
                {DEMO_IMAGES.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.caption}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                
                {/* Demo overlay */}
                <div className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2 py-1 rounded font-medium animate-pulse">
                  LIVE DEMO
                </div>
                
                {/* Controls */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={prevSlide}
                        className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 text-white" />
                      </button>
                      <button
                        onClick={() => setIsPlaying(!isPlaying)}
                        className="w-8 h-8 flex items-center justify-center bg-cyan-500 hover:bg-cyan-600 rounded-full transition-colors"
                      >
                        {isPlaying ? (
                          <div className="w-2 h-2 bg-white rounded-sm" />
                        ) : (
                          <Play className="w-4 h-4 text-white ml-0.5" />
                        )}
                      </button>
                      <button
                        onClick={nextSlide}
                        className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 text-white" />
                      </button>
                    </div>
                    <span className="text-white/80 text-sm">
                      {DEMO_IMAGES[currentSlide].caption}
                    </span>
                  </div>
                  
                  {/* Progress dots */}
                  <div className="flex justify-center gap-2 mt-3">
                    {DEMO_IMAGES.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentSlide 
                            ? 'bg-cyan-400 w-6' 
                            : 'bg-white/40 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* TV stand */}
              <div className="flex justify-center mt-2">
                <div className="w-20 h-2 bg-slate-700 rounded-full" />
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl" />
          </div>

          {/* Features */}
          <div className="space-y-8">
            <div className="grid gap-6">
              <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Easy Content Management</h3>
                  <p className="text-slate-400 text-sm">Upload images and videos through our simple admin panel. No technical skills required.</p>
                </div>
              </div>
              
              <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Multiple Locations</h3>
                  <p className="text-slate-400 text-sm">Manage displays across different locations from one central dashboard.</p>
                </div>
              </div>
              
              <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Scheduled Playlists</h3>
                  <p className="text-slate-400 text-sm">Set different content for different times of day automatically.</p>
                </div>
              </div>
              
              <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Wifi className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Remote Monitoring</h3>
                  <p className="text-slate-400 text-sm">See which displays are online and track their status in real-time.</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3"
              >
                <a href="/test" target="_blank" rel="noopener noreferrer">
                  <Play className="w-4 h-4 mr-2" />
                  View Full Demo
                </a>
              </Button>
              <Button 
                asChild
                variant="outline" 
                className="border-slate-600 text-slate-300 hover:bg-slate-800 px-6 py-3"
              >
                <a href="#contact">
                  Get a Quote
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* How it Works Section */}
        <div className="mt-20 pt-16 border-t border-slate-800">
          <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            How It <span className="text-cyan-400">Works</span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {WORKFLOW_STEPS.map((item, index) => (
              <div 
                key={item.step}
                className="relative text-center group"
              >
                {/* Connector line */}
                {index < WORKFLOW_STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent" />
                )}
                
                {/* Step number */}
                <div className="relative inline-flex items-center justify-center w-24 h-24 mb-6">
                  <div className="absolute inset-0 bg-cyan-500/10 rounded-2xl transform rotate-6 group-hover:rotate-12 transition-transform" />
                  <div className="absolute inset-0 bg-slate-800 rounded-2xl border border-slate-700 group-hover:border-cyan-500/50 transition-colors" />
                  <item.icon className="relative w-10 h-10 text-cyan-400" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {item.step}
                  </span>
                </div>
                
                <h4 className="text-white font-semibold text-lg mb-2">{item.title}</h4>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-slate-400 mb-4">Ready to transform your displays?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={playVideoDemo}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-3"
              >
                <Play className="w-4 h-4 mr-2" />
                Watch Narrated Demo
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 px-8 py-3"
              >
                <a href="#contact">
                  Contact Us Today
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Video Demo Modal */}
        {showVideoDemo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
            <div className="relative w-full max-w-4xl">
              {/* Close button */}
              <button
                onClick={closeVideoDemo}
                className="absolute -top-12 right-0 text-white hover:text-cyan-400 text-lg font-medium"
              >
                ✕ Close
              </button>
              
              {/* Video container */}
              <div className="relative bg-slate-900 rounded-xl overflow-hidden shadow-2xl">
                <video
                  ref={videoRef}
                  className="w-full aspect-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  onError={(e) => console.error('Video error:', e)}
                >
                  <source 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/media/demo_video.mp4`} 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
                
                {/* Hidden audio for narration */}
                <audio
                  ref={audioRef}
                  loop
                >
                  <source 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/media/demo_narration.mp3`} 
                    type="audio/mpeg" 
                  />
                </audio>
                
                {/* Controls overlay */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={togglePause}
                        className="w-10 h-10 flex items-center justify-center bg-cyan-500 hover:bg-cyan-600 rounded-full transition-colors"
                      >
                        {videoPaused ? (
                          <Play className="w-5 h-5 text-white ml-0.5" />
                        ) : (
                          <Pause className="w-5 h-5 text-white" />
                        )}
                      </button>
                      <button
                        onClick={toggleMute}
                        className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                      >
                        {videoMuted ? (
                          <VolumeX className="w-5 h-5 text-white" />
                        ) : (
                          <Volume2 className="w-5 h-5 text-white" />
                        )}
                      </button>
                      <span className="text-white/80 text-sm ml-2">
                        {videoMuted ? 'Click speaker for narration' : 'Narration playing'}
                      </span>
                    </div>
                    <span className="text-cyan-400 text-sm font-medium">
                      FrostByte IT Digital Signage Demo
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Caption */}
              <p className="text-center text-slate-400 text-sm mt-4">
                See how easy it is to manage your digital displays
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DigitalSignage;
